import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CaseStatus, EndReasonId, SystemEndReason, } from '@debbie/common/dist/cases/index.js';
import { BulkActionType, } from '@debbie/common/dist/insights/bulk-actions.js';
import { h } from '@debbie/cortex/dist/index.js';
import * as insightsAPI from '@debbie/cortex/dist/api/insights.js';
import { useBillingModels } from '@debbie/cortex/dist/billing-models/index.js';
import { selectProcedures } from '@debbie/cortex/dist/procedures/selectors.js';
import { selectUpdateTypes, selectUpdateTypesMap, } from '@debbie/cortex/dist/update-types/selectors.js';
import { selectWorkflows } from '@debbie/cortex/dist/workflows/selectors.js';
import { selectExclusiveContent } from '@debbie/cortex/dist/exclusiveContent/selectors.js';
import Button from '../Button.js';
import SearchInput from '../SearchInput.js';
import Select from '../Select.js';
import Well from '../Well.js';
import { selectMe } from '@debbie/cortex/dist/me/selectors.js';
import { useBillingAppendixSetups } from '@debbie/cortex/dist/billing-appendix-setups/index.js';
import { useEndReasons } from '@debbie/cortex/dist/end-reasons/index.js';
import { useCaseGroups } from '@debbie/cortex/dist/caseGroups/index.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
import { TenantType } from '@debbie/common/dist/tenants/index.js';
const BulkActions = (props) => {
    const { caseFilter } = props;
    const lang = useSelector((s) => s.lang);
    const procedures = useSelector(selectProcedures);
    const exclusiveContent = useSelector(selectExclusiveContent);
    const me = useSelector(selectMe);
    const billingModelsState = useBillingModels();
    const billingModels = h.hasData(billingModelsState)
        ? billingModelsState.data
        : null;
    const billingAppendixSetupsState = useBillingAppendixSetups();
    const billingAppendixSetups = h.hasData(billingAppendixSetupsState)
        ? billingAppendixSetupsState.data
        : null;
    const workflows = useSelector(selectWorkflows);
    const tenant = useSelector(selectTenant);
    const updateTypes = useSelector(selectUpdateTypes);
    const updateTypesMap = useSelector(selectUpdateTypesMap);
    const caseGroupsLoadable = useCaseGroups();
    const caseGroups = h.hasData(caseGroupsLoadable)
        ? caseGroupsLoadable.data
        : null;
    const endReasons = useEndReasons();
    const [loadingBulkAction, setLoadingBulkAction] = useState(false);
    const [bulkActionStarted, setBulkActionStarted] = useState(false);
    const [bulkActionType, setBulkAction] = useState(null);
    const [caseStatus, setCaseStatus] = useState(CaseStatus.VERIFIED);
    const [endReason, setEndReason] = useState(EndReasonId[SystemEndReason.ErrorCreditor]);
    const [updateTypeId, setUpdateTypeId] = useState(null);
    const [procedureId, setProcedureId] = useState(null);
    const [billingModelId, setBillingModelId] = useState(null);
    const [billingAppendixSetupId, setBillingAppendixSetupId] = useState(null);
    const [workflowId, setWorkflowId] = useState(null);
    const [targetTaskId, setTargetTaskId] = useState(null);
    const [caseGroupId, setCaseGroupId] = useState(null);
    const defaultUpdateType = updateTypeId && updateTypesMap[updateTypeId];
    const selectedProcedure = procedures && procedures.find((p) => p.procedureId === procedureId);
    const selectedBillingModel = billingModels &&
        billingModels.find((p) => p.billingModelId === billingModelId);
    const selectedBillingAppendixSetup = billingAppendixSetups &&
        billingAppendixSetups.find((p) => p.billingAppendixSetupId === billingAppendixSetupId);
    const selectedWorkflow = workflows &&
        workflows.find((workflow) => workflow.workflowId === workflowId);
    const inWorkflowIds = caseFilter.inWorkflowIds;
    const selectedWorkflows = inWorkflowIds
        ? workflows?.filter((workflow) => inWorkflowIds.includes(workflow.workflowId))
        : workflows;
    const tasks = selectedWorkflows
        ?.reduce((acc, workflow) => {
        for (let i = 0; i < workflow.tasks.length; i++) {
            const task = workflow.tasks[i];
            const updateType = updateTypesMap && updateTypesMap[task.updateTypeId];
            if (updateType) {
                acc = acc.concat({
                    task,
                    updateType,
                    position: task.position,
                });
            }
        }
        return acc;
    }, [])
        .sort((a, b) => a.position.y - b.position.y);
    const selectedTargetTask = tasks?.find((task) => task.task.id === targetTaskId);
    const selectedCaseGroup = caseGroups?.find((caseGroup) => caseGroup.id === caseGroupId);
    useEffect(() => {
        setBulkActionStarted(false);
    }, [caseFilter]);
    const searchUpdateTypes = (q) => {
        if (!updateTypes)
            return [];
        return updateTypes
            .filter((uT) => {
            return (`${uT.label}${lang.updateTypes[uT.updateTypeId]}`
                .toLocaleLowerCase()
                .indexOf(q.toLocaleLowerCase()) !== -1);
        })
            .map((uT) => ({
            heading: lang.updateTypes[uT.updateTypeId] || uT.label,
            subheading: uT.groupLabel,
            key: uT.updateTypeId,
            returnData: uT,
        }))
            .sort((a, b) => (a.heading > b.heading ? 1 : 0) - (a.heading < b.heading ? 1 : 0));
    };
    const bulkActionEditors = {
        [BulkActionType.MoveCaseStatus]: (React.createElement("div", null,
            React.createElement(Select, { value: caseStatus, onChange: (e) => setCaseStatus(e.target.value) },
                React.createElement("option", { value: CaseStatus.VERIFIED }, lang.cases[CaseStatus.VERIFIED]),
                React.createElement("option", { value: CaseStatus.ENDED }, lang.cases[CaseStatus.ENDED])),
            CaseStatus.ENDED === caseStatus && (React.createElement(Select, { value: endReason, onChange: (e) => setEndReason(e.target.value) }, (h.hasData(endReasons) ? endReasons.data : []).map((reason) => (React.createElement("option", { key: reason.id, value: reason.id }, lang.endReason[reason.handle] ??
                reason.label))))))),
        [BulkActionType.PauseCases]: (React.createElement(SearchInput, { autofocus: true, onSelect: (uT) => {
                setUpdateTypeId(uT.updateTypeId);
            }, onRemoveStatic: () => {
                setUpdateTypeId(null);
            }, forceOpen: true, static: defaultUpdateType
                ? lang.updateTypes[defaultUpdateType.updateTypeId] ||
                    defaultUpdateType.label
                : null, searchDelay: 0, searchAll: searchUpdateTypes, placeholder: lang.cases.pauseReason, cacheResetTrigger: BulkActionType.PauseCases })),
        [BulkActionType.ContinueCases]: (React.createElement(SearchInput, { autofocus: true, onSelect: (uT) => {
                setUpdateTypeId(uT.updateTypeId);
            }, onRemoveStatic: () => {
                setUpdateTypeId(null);
            }, forceOpen: true, static: defaultUpdateType
                ? lang.updateTypes[defaultUpdateType.updateTypeId] ||
                    defaultUpdateType.label
                : null, searchDelay: 0, searchAll: searchUpdateTypes, placeholder: lang.cases.pauseReason, cacheResetTrigger: BulkActionType.ContinueCases })),
        [BulkActionType.RunProcedure]: (React.createElement(SearchInput, { forceOpen: true, placeholder: lang.procedures.selectedProcedure, onSelect: (id) => setProcedureId(id), searchAll: (q) => {
                return (procedures || [])
                    .filter((procedure) => `${JSON.stringify(procedure)}`
                    .toLowerCase()
                    .indexOf(q.toLowerCase()) !== -1 || q.length === 0)
                    .map((procedure) => ({
                    key: procedure.procedureId,
                    heading: procedure.title,
                    subheading: procedure.description,
                    returnData: procedure.procedureId,
                }));
            }, static: selectedProcedure && selectedProcedure.title, onRemoveStatic: () => setProcedureId(null), cacheResetTrigger: BulkActionType.RunProcedure })),
        [BulkActionType.ChangeBillingModel]: (React.createElement(SearchInput, { forceOpen: true, placeholder: lang.bulkActions.bulkActionBillingModelPlaceholder, onSelect: (id) => setBillingModelId(id), searchAll: (q) => {
                return (billingModels || [])
                    .filter((billingAppendixSetup) => !billingAppendixSetup.archived)
                    .filter((billingModel) => `${JSON.stringify(billingModel)}`
                    .toLowerCase()
                    .indexOf(q.toLowerCase()) !== -1 || q.length === 0)
                    .map((billingModel) => ({
                    key: billingModel.billingModelId,
                    heading: billingModel.name,
                    returnData: billingModel.billingModelId,
                }));
            }, static: selectedBillingModel && selectedBillingModel.name, onRemoveStatic: () => setBillingModelId(null), cacheResetTrigger: BulkActionType.ChangeBillingModel })),
        [BulkActionType.ChangeBillingAppendixSetup]: (React.createElement(SearchInput, { forceOpen: true, placeholder: lang.billingAppendixSetups.selectBillingAppendixSetup, onSelect: (id) => setBillingAppendixSetupId(id), searchAll: (q) => {
                return (billingAppendixSetups || [])
                    .filter((billingAppendixSetup) => `${JSON.stringify(billingAppendixSetup)}`
                    .toLowerCase()
                    .indexOf(q.toLowerCase()) !== -1 || q.length === 0)
                    .map((billingAppendixSetup) => ({
                    key: billingAppendixSetup.billingAppendixSetupId,
                    heading: billingAppendixSetup.name,
                    returnData: billingAppendixSetup.billingAppendixSetupId,
                }));
            }, static: selectedBillingAppendixSetup && selectedBillingAppendixSetup.name, onRemoveStatic: () => setBillingAppendixSetupId(null), cacheResetTrigger: BulkActionType.ChangeBillingAppendixSetup })),
        [BulkActionType.ChangeWorkflow]: (React.createElement(SearchInput, { forceOpen: true, placeholder: lang.bulkActions.bulkActionWorkflowPlaceholder, onSelect: (id) => setWorkflowId(id), searchAll: (q) => {
                return (workflows || [])
                    .filter((workflow) => `${JSON.stringify(workflow)}`
                    .toLowerCase()
                    .indexOf(q.toLowerCase()) !== -1 || q.length === 0)
                    .map((workflow) => ({
                    key: workflow.workflowId,
                    heading: workflow.title,
                    subheading: workflow.description,
                    returnData: workflow.workflowId,
                }));
            }, static: selectedWorkflow && selectedWorkflow.title, onRemoveStatic: () => setWorkflowId(null), cacheResetTrigger: BulkActionType.ChangeWorkflow })),
        [BulkActionType.MoveCaseToWorkflowTask]: inWorkflowIds?.length === 1 ? (React.createElement(SearchInput, { forceOpen: true, placeholder: lang.bulkActions.bulkActionMoveToTaskInWorkflowPlaceholder, onSelect: (id) => setTargetTaskId(id), searchAll: (q) => {
                return (tasks || [])
                    .filter((task) => `${JSON.stringify(task)}`
                    .toLowerCase()
                    .indexOf(q.toLowerCase()) !== -1 || q.length === 0)
                    .map((task) => ({
                    key: task.task.id,
                    heading: task.updateType.label,
                    returnData: task.task.id,
                }));
            }, static: selectedTargetTask && selectedTargetTask.updateType.label, onRemoveStatic: () => setTargetTaskId(null), cacheResetTrigger: BulkActionType.MoveCaseToWorkflowTask })) : (React.createElement("p", null, lang.bulkActions.pleaseSelectAWorkflow)),
        [BulkActionType.RollbackOneTask]: React.createElement(React.Fragment, null),
        [BulkActionType.RollbackAllTasks]: React.createElement(React.Fragment, null),
        [BulkActionType.RunBillingModel]: React.createElement(React.Fragment, null),
        [BulkActionType.ResubscribeCosmo]: React.createElement(React.Fragment, null),
        [BulkActionType.ResyncCosmo]: React.createElement(React.Fragment, null),
        [BulkActionType.ChangeCaseGroup]: (React.createElement(SearchInput, { forceOpen: true, placeholder: lang.bulkActions.bulkActionChangeCaseGroup, onSelect: (id) => setCaseGroupId(id), searchAll: (q) => {
                return (caseGroups || [])
                    .filter((task) => `${JSON.stringify(task)}`
                    .toLowerCase()
                    .indexOf(q.toLowerCase()) !== -1 || q.length === 0)
                    .map((caseGroup) => ({
                    key: caseGroup.id,
                    heading: caseGroup.label,
                    returnData: caseGroup.id,
                }));
            }, static: selectedCaseGroup && selectedCaseGroup.label, onRemoveStatic: () => setCaseGroupId(null), cacheResetTrigger: BulkActionType.ChangeCaseGroup })),
    };
    const getBulkAction = () => {
        if (!bulkActionType)
            return null;
        switch (bulkActionType) {
            case BulkActionType.MoveCaseStatus:
                if (caseStatus === CaseStatus.ENDED) {
                    return {
                        bulkAction: {
                            type: BulkActionType.MoveCaseStatus,
                            details: {
                                status: CaseStatus.ENDED,
                                endReasonId: endReason,
                            },
                        },
                    };
                }
                if (caseStatus === CaseStatus.VERIFIED) {
                    return {
                        bulkAction: {
                            type: BulkActionType.MoveCaseStatus,
                            details: {
                                status: CaseStatus.VERIFIED,
                                endReasonId: null,
                            },
                        },
                    };
                }
                return null;
            case BulkActionType.PauseCases:
                if (!updateTypeId)
                    return null;
                return {
                    bulkAction: {
                        type: BulkActionType.PauseCases,
                        details: {
                            updateTypeId,
                        },
                    },
                };
            case BulkActionType.ContinueCases:
                if (!updateTypeId)
                    return null;
                return {
                    bulkAction: {
                        type: BulkActionType.ContinueCases,
                        details: {
                            updateTypeId,
                        },
                    },
                };
            case BulkActionType.RunProcedure:
                if (!procedureId)
                    return null;
                return {
                    bulkAction: {
                        type: BulkActionType.RunProcedure,
                        details: { procedureId },
                    },
                };
            case BulkActionType.ChangeBillingModel:
                if (!billingModelId)
                    return null;
                return {
                    bulkAction: {
                        type: BulkActionType.ChangeBillingModel,
                        details: { billingModelId },
                    },
                };
            case BulkActionType.ChangeBillingAppendixSetup:
                if (!billingAppendixSetupId)
                    return null;
                return {
                    bulkAction: {
                        type: BulkActionType.ChangeBillingAppendixSetup,
                        details: { billingAppendixSetupId },
                    },
                };
            case BulkActionType.ChangeWorkflow:
                if (!workflowId)
                    return null;
                return {
                    bulkAction: {
                        type: BulkActionType.ChangeWorkflow,
                        details: { workflowId },
                    },
                };
            case BulkActionType.MoveCaseToWorkflowTask:
                if (!(inWorkflowIds?.length === 1 && targetTaskId))
                    return null;
                if (!(caseFilter?.inWorkflowIds && caseFilter?.inWorkflowIds[0])) {
                    return null;
                }
                return {
                    bulkAction: {
                        type: BulkActionType.MoveCaseToWorkflowTask,
                        details: {
                            workflowId: caseFilter.inWorkflowIds[0],
                            targetTaskId,
                        },
                    },
                };
            case BulkActionType.RollbackOneTask:
                return {
                    bulkAction: {
                        type: BulkActionType.RollbackOneTask,
                        details: {},
                    },
                };
            case BulkActionType.RollbackAllTasks:
                return {
                    bulkAction: {
                        type: BulkActionType.RollbackAllTasks,
                        details: {},
                    },
                };
            case BulkActionType.RunBillingModel:
                return {
                    bulkAction: {
                        type: BulkActionType.RunBillingModel,
                        details: {},
                    },
                };
            case BulkActionType.ResubscribeCosmo:
                return {
                    bulkAction: {
                        type: BulkActionType.ResubscribeCosmo,
                        details: {},
                    },
                };
            case BulkActionType.ResyncCosmo:
                return {
                    bulkAction: {
                        type: BulkActionType.ResyncCosmo,
                        details: {},
                    },
                };
            case BulkActionType.ChangeCaseGroup:
                if (!caseGroupId)
                    return null;
                return {
                    bulkAction: {
                        type: BulkActionType.ChangeCaseGroup,
                        details: {
                            caseGroupId,
                        },
                    },
                };
        }
    };
    const hasExclusiveContent = !!(me && exclusiveContent?.includes(me.id));
    return (React.createElement("div", null,
        React.createElement(Select, { value: bulkActionType, onChange: (e) => {
                setBulkActionStarted(false);
                setBulkAction(e.target.value);
            } },
            React.createElement("option", { value: "" },
                "- ",
                lang.bulkActions.bulkActions,
                " -"),
            Object.values(BulkActionType).map((_bulkActionType) => {
                if ([
                    BulkActionType.RunBillingModel,
                    BulkActionType.ResubscribeCosmo,
                    BulkActionType.ResyncCosmo,
                ].includes(_bulkActionType) &&
                    !hasExclusiveContent) {
                    return null;
                }
                // Hide for tenant.type=AccountsReceivable
                if ([
                    BulkActionType.ChangeBillingModel,
                    BulkActionType.ChangeBillingAppendixSetup,
                ].includes(_bulkActionType) &&
                    tenant?.type === TenantType.AccountsReceivable &&
                    !hasExclusiveContent) {
                    return null;
                }
                return (React.createElement("option", { key: _bulkActionType, value: _bulkActionType }, lang.bulkActionTypes[_bulkActionType]));
            })),
        bulkActionType && (React.createElement(Well, null, bulkActionStarted ? (React.createElement("div", null,
            React.createElement("p", null, lang.bulkActions.bulkActionStarted))) : (React.createElement("div", null,
            React.createElement("p", null, lang.bulkActionDescriptions[bulkActionType]),
            bulkActionEditors[bulkActionType],
            React.createElement(Button, { loading: loadingBulkAction, disabled: !getBulkAction(), onClick: async () => {
                    setLoadingBulkAction(true);
                    if (confirm(lang.bulkActions.bulkActionsConfirm)) {
                        try {
                            const action = getBulkAction();
                            if (!action)
                                throw Error('Error');
                            await insightsAPI.caseFilterBulk({ ...caseFilter, ...action?.caseFilter }, action?.bulkAction);
                            setBulkActionStarted(true);
                        }
                        catch (error) {
                            console.error(error);
                            alert('Error');
                        }
                    }
                    setLoadingBulkAction(false);
                }, block: true }, lang.bulkActions.runBulkAction)))))));
};
export default BulkActions;
//# sourceMappingURL=BulkActions.js.map